import { hasRTSPPublication } from "../utils/streamOutputs";
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Drawer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery
} from "@mui/material";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import CloseIcon from "@mui/icons-material/Close";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import TableRowsIcon from "@mui/icons-material/TableRows";
import AppsIcon from "@mui/icons-material/Apps";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import AddBoxIcon from '@mui/icons-material/AddBox';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vscodeTheme } from "@uiw/react-json-view/vscode";
import { lightTheme } from "@uiw/react-json-view/light";
import StreamFormDialog from "../components/StreamFormDialog";
import StreamInfoDialog from "../components/StreamInfoDialog";
import StreamEventsDrawer from "../components/StreamEventsDrawer";
import StreamCalendar from "../components/StreamCalendar";
import StreamCards from "../components/StreamCards";
import StreamMasonry from "../components/StreamMasonry";
import StreamTable from "../components/StreamTable";
import { extractRecastOutputs, isRecastMode, type StreamOutputStatsSnapshot } from "../utils/streamOutputs";
import { isStreamConfigReadOnly, isStreamRunningState, isStreamStartingState } from "../utils/streamState";
import { useStreamsState } from "../context/StreamsStateContext";
import { useStreamOutputsState } from "../context/StreamOutputsStateContext";
import { useAuth } from "../context/AuthContext";
import { useStreamDetectionInfo } from "../hooks/useStreamDetectionInfo";
import { subscribeToStreamListChanges, subscribeToPlatformListChanges } from "../context/AppContext";
import type { StreamSummary } from "../types/streamSummary";
import type { FileSourceInfoSnapshot } from "../types/fileSourceInfo";
import { isReplayFileSource } from "../utils/streamFileSource";
import { buildBitrateChart, normalizeBitrateSamples, fetchBitrateHistory, GRID_BITRATE_WINDOW_MS, type BitrateChartData, type BitrateClockAnchor } from "../utils/bitrateHistory";
import { useBitrateClock } from "../hooks/useBitrateClock";
import { visibleStreamBitrates } from "../utils/streamBitratePresentation";

type PlatformOption = {
  id: string;
  name: string;
};

type StreamBitrateResponse = {
  streamId: string;
  samples?: unknown;
  bitrateMeasurementsMax: number;
};

type StreamsBitratesResponse = BitrateClockAnchor & {
  streams?: StreamBitrateResponse[];
  error?: string;
};

type StreamFileSourceInfoEntry = {
  streamId: string;
  progress?: number | null;
  position?: number | null;
  loopCount?: number | null;
};

type StreamsFileSourceInfosResponse = {
  streams?: StreamFileSourceInfoEntry[];
  error?: string;
};

type StreamOutputStatusEntry = {
  whip?: import("../utils/streamOutputs").WhipStats;
  lastError?: string;
  playbackUrl?: string;
  metadataUrl?: string;
  segmentFormat?: string;
  sei?: import("../utils/streamOutputs").SeiStats;
  publicationState?: string;
  clientCount?: number;
  deliveryFormat?: string;
  outputId: string;
  bitrate?: number | null;
  bytesSent?: number | null;
};

type StreamOutputStatusesResponse = {
  streams?: { streamId: string; outputs?: StreamOutputStatusEntry[] }[];
  error?: string;
};

type StreamDeleteProgressPayload = {
  jobId: string;
  total: number;
  completed: number;
  failed: number;
  status: "running" | "completed" | "failed";
  currentId?: string;
  currentError?: string;
};

type StreamOutputStatsByStream = Map<string, Map<string, StreamOutputStatsSnapshot>>;

const DEFAULT_NETWORK_INTERFACE_OPTIONS: string[] = ["0.0.0.0", "127.0.0.1"];
type StreamsViewMode = "Cards" | "Table" | "Masonry";
const STREAMS_VIEW_MODE_STORAGE_KEY = "streamsPageViewMode";
const DEFAULT_STREAMS_VIEW_MODE: StreamsViewMode = "Table";

const parseStoredStreamsViewMode = (value: string | null): StreamsViewMode | null => {
  if (value === "Cards" || value === "Table" || value === "Masonry") {
    return value;
  }
  return null;
};
const OUTPUT_STATUS_REFRESH_MS = 2000;
const FILE_SOURCE_INFO_REFRESH_MS = 2000;
const DEFAULT_OUTPUT_OFFLINE_AFTER_MS = 2000;

const StreamsPage = () => {
  const { authorizedFetch, user, tokens, userManagementEnabled, resolveApiPath } = useAuth();
  const theme = useTheme();
  const isNarrowToolbar = useMediaQuery(theme.breakpoints.down("md"));
  const canManageStreams = !userManagementEnabled || user?.role === "admin";
  const canViewDetectionInfo = !userManagementEnabled || Boolean(user) || Boolean(tokens?.accessToken);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery<StreamSummary[]>({
    queryKey: ["streams"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/streams");
      const payload = (await response.json().catch(() => null)) as { streams?: StreamSummary[]; error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load streams");
      }
      return payload?.streams ?? [];
    }
  });

  const streams = useMemo(() => data ?? [], [data]);
  const hasFileSourceStreams = useMemo(
    () => streams.some(isReplayFileSource),
    [streams]
  );
  const hasRecastStreams = useMemo(
    () =>
      streams.some((stream) => isRecastMode(stream.mode)),
    [streams]
  );
  const { getStreamState, getStreamStateMessage, setStreamState } = useStreamsState();
  const { getOutputState, getOutputStateMessage } = useStreamOutputsState();
  const {
    detectionStream,
    detectionData,
    detectionStateLabel,
    showDetectingSpinner,
    isDetectionLoading,
    detectionErrorMessage,
    openDetection,
    closeDetection
  } = useStreamDetectionInfo({ enabled: canViewDetectionInfo });
  const jsonViewTheme = useMemo(() => (theme.palette.mode === "dark" ? vscodeTheme : lightTheme), [theme.palette.mode]);
  const [fileSourceInfoByStreamId, setFileSourceInfoByStreamId] = useState<Map<string, FileSourceInfoSnapshot>>(() => new Map());
  const [streamOutputStatsById, setStreamOutputStatsById] = useState<StreamOutputStatsByStream>(() => new Map());

  const lastBitrateRefreshMs = useMemo(() => {
    if (streams.length <= 25) {
      return 2000;
    }
    if (streams.length <= 50) {
      return 3000;
    }
    return 4000;
  }, [streams.length]);

  const bitratesQuery = useQuery<StreamsBitratesResponse>({
    queryKey: ["streams", "timestamped-bitrates"],
    queryFn: ({ signal }) => fetchBitrateHistory<StreamsBitratesResponse>(authorizedFetch, "/api/streams/bitrates", signal),
    refetchInterval: lastBitrateRefreshMs,
    refetchIntervalInBackground: true,
    refetchOnMount: "always",
  });
  const bitrateNow = useBitrateClock(bitratesQuery.data);
  const streamBitratesById = useMemo(() => new Map(
    (bitratesQuery.data?.streams ?? []).map((entry) => [entry.streamId, normalizeBitrateSamples(entry.samples)]),
  ), [bitratesQuery.data]);
  const visibleStreamBitratesById = useMemo(() => {
    const visible = new Map<string, BitrateChartData>();
    streams.forEach((stream) => {
      const samples = visibleStreamBitrates(
        streamBitratesById.get(stream.id) ?? [],
        (isStreamRunningState(getStreamState(stream.id), getStreamStateMessage(stream.id)) || hasRTSPPublication(streamOutputStatsById.get(stream.id))),
      );
      visible.set(stream.id, buildBitrateChart(samples, bitrateNow, GRID_BITRATE_WINDOW_MS));
    });
    return visible;
  }, [getStreamState, getStreamStateMessage, streamBitratesById, streams, bitrateNow, streamOutputStatsById]);

  const outputStatusesQuery = useQuery<StreamOutputStatusesResponse>({
    queryKey: ["streams", "output-stats"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/streams/output_stats", { cache: "no-store" });
      const payload = (await response.json().catch(() => null)) as StreamOutputStatusesResponse | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load stream output stats");
      }
      return payload ?? { streams: [] };
    },
    enabled: hasRecastStreams,
    refetchInterval: OUTPUT_STATUS_REFRESH_MS,
    refetchIntervalInBackground: true,
    placeholderData: keepPreviousData,
  });

  const fileSourceInfosQuery = useQuery<StreamsFileSourceInfosResponse>({
    queryKey: ["streams", "file-source-infos"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/streams/file_source_infos");
      const payload = (await response.json().catch(() => null)) as StreamsFileSourceInfosResponse | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load stream file source info");
      }
      return payload ?? { streams: [] };
    },
    enabled: hasFileSourceStreams,
    refetchInterval: FILE_SOURCE_INFO_REFRESH_MS,
    refetchIntervalInBackground: true,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!hasFileSourceStreams) {
      setFileSourceInfoByStreamId(new Map());
      return;
    }

    const entries = fileSourceInfosQuery.data?.streams ?? [];
    setFileSourceInfoByStreamId(() => {
      const next = new Map<string, FileSourceInfoSnapshot>();
      entries.forEach((entry) => {
        const streamId = typeof entry?.streamId === "string" ? entry.streamId.trim() : "";
        if (!streamId) {
          return;
        }

        const progress = Number(entry.progress ?? Number.NaN);
        const position = Number(entry.position ?? Number.NaN);
        const loopCount = Number(entry.loopCount ?? Number.NaN);
        next.set(streamId, {
          ...(Number.isFinite(progress) ? { progress } : {}),
          ...(Number.isFinite(position) ? { position } : {}),
          ...(Number.isFinite(loopCount) ? { loopCount } : {}),
        });
      });
      return next;
    });
  }, [hasFileSourceStreams, fileSourceInfosQuery.data]);

  useEffect(() => {
    if (!hasRecastStreams) {
      setStreamOutputStatsById(new Map());
      return;
    }

    const entries = outputStatusesQuery.data?.streams ?? [];
    const payloadByStream = new Map<string, Map<string, StreamOutputStatusEntry>>();
    entries.forEach((entry) => {
      const streamId = typeof entry?.streamId === "string" ? entry.streamId.trim() : "";
      if (!streamId) {
        return;
      }

      const outputs = new Map<string, StreamOutputStatusEntry>();
      (entry.outputs ?? []).forEach((output) => {
        const outputId = typeof output.outputId === "string" ? output.outputId.trim() : "";
        if (!outputId) {
          return;
        }
        outputs.set(outputId, output);
      });
      payloadByStream.set(streamId, outputs);
    });

    setStreamOutputStatsById((previous) => {
      const now = Date.now();
      const next: StreamOutputStatsByStream = new Map();
      streams.forEach((stream) => {
        const streamId = typeof stream.id === "string" ? stream.id.trim() : "";
        if (!streamId) {
          return;
        }

        const configuredOutputs = extractRecastOutputs(stream.targets);
        if (!configuredOutputs.length) {
          return;
        }

        const timeoutMs = DEFAULT_OUTPUT_OFFLINE_AFTER_MS;

        const previousOutputs = previous.get(streamId) ?? new Map<string, StreamOutputStatsSnapshot>();
        const payloadOutputs = payloadByStream.get(streamId) ?? new Map<string, StreamOutputStatusEntry>();
        const outputs = new Map<string, StreamOutputStatsSnapshot>();

        configuredOutputs.forEach((configuredOutput) => {
          const outputId = typeof configuredOutput.id === "string" ? configuredOutput.id.trim() : "";
          if (!outputId) {
            return;
          }

          const previousStats = previousOutputs.get(outputId);
          const payload = payloadOutputs.get(outputId);
          const bitrate = Number(payload?.bitrate ?? 0);
          const payloadBytesSent = Number(payload?.bytesSent ?? Number.NaN);
          const hasCurrentBitrate = Number.isFinite(bitrate) && bitrate > 0;
          const hasPayloadBytesSent = Number.isFinite(payloadBytesSent);
          const bytesSent = Number.isFinite(payloadBytesSent) ? payloadBytesSent : (previousStats?.bytesSent ?? 0);

          let lastBytesChangedAt = previousStats?.lastBytesChangedAt ?? now;
          if (!previousStats) {
            lastBytesChangedAt = now;
          } else if (hasPayloadBytesSent && bytesSent !== previousStats.bytesSent) {
            lastBytesChangedAt = now;
          } else if (hasCurrentBitrate) {
            lastBytesChangedAt = now;
          }

          const bytesTimedOut = now - lastBytesChangedAt >= timeoutMs;

          outputs.set(outputId, {
            playbackUrl: payload?.playbackUrl, metadataUrl: payload?.metadataUrl,
            sei: payload?.sei, segmentFormat: payload?.segmentFormat, publicationState: payload?.publicationState, clientCount: payload?.clientCount, deliveryFormat: payload?.deliveryFormat,
            whip: payload?.whip, lastError: payload?.lastError,
            bitrate: bytesTimedOut ? 0 : (Number.isFinite(bitrate) ? bitrate : 0),
            bytesSent,
            bytesTimedOut,
            lastBytesChangedAt,
          });
        });

        if (outputs.size > 0) {
          next.set(streamId, outputs);
        }
      });
      return next;
    });
  }, [hasRecastStreams, outputStatusesQuery.data, outputStatusesQuery.dataUpdatedAt, streams]);

  const {
    data: platformsData,
    isLoading: isPlatformsLoading,
    isError: isPlatformsError,
    error: platformsError
  } = useQuery<PlatformOption[]>({
    queryKey: ["platforms", "options"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/platforms");
      const payload = (await response.json().catch(() => null)) as
        | { platforms?: PlatformOption[]; error?: string }
        | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load platforms");
      }
      return (payload?.platforms ?? []).map((platform) => ({ id: platform.id, name: platform.name }));
    }
  });

  const platformOptions = useMemo(() => platformsData ?? [], [platformsData]);

  const [selectedPlatformIds, setSelectedPlatformIds] = useState<string[]>([]);
  const selectedPlatformOptions = useMemo(
    () => platformOptions.filter((option) => selectedPlatformIds.includes(option.id)),
    [platformOptions, selectedPlatformIds]
  );

  const platformLoadErrorMessage = useMemo(() => {
    if (!isPlatformsError) {
      return null;
    }
    if (platformsError instanceof Error) {
      return platformsError.message;
    }
    if (typeof platformsError === "string") {
      return platformsError;
    }
    return "Unable to load platforms";
  }, [isPlatformsError, platformsError]);

  const handlePlatformFilterChange = useCallback(
    (_event: SyntheticEvent, value: PlatformOption[]) => {
      setSelectedPlatformIds(value.map((option) => option.id));
    },
    []
  );

  const handleViewModeChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, nextValue: StreamsViewMode | null) => {
      if (!nextValue) {
        return;
      }
      setStreamsViewMode(nextValue);
    },
    []
  );

  useEffect(() => {
    setSelectedPlatformIds((current) => {
      if (current.length === 0) {
        return current;
      }
      const validIds = new Set(platformOptions.map((option) => option.id));
      const next = current.filter((id) => validIds.has(id));
      if (next.length === current.length) {
        return current;
      }
      return next;
    });
  }, [platformOptions]);

  const filteredStreams = useMemo(() => {
    if (selectedPlatformIds.length === 0) {
      return streams;
    }
    const selectedIds = new Set(selectedPlatformIds);
    return streams.filter((stream) => stream.platformId && selectedIds.has(stream.platformId));
  }, [streams, selectedPlatformIds]);

  const hasPlatformFilter = selectedPlatformIds.length > 0;

  const [streamsViewMode, setStreamsViewMode] = useState<StreamsViewMode>(() => {
    if (typeof window !== "undefined") {
      const storedMode = parseStoredStreamsViewMode(window.localStorage.getItem(STREAMS_VIEW_MODE_STORAGE_KEY));
      if (storedMode) {
        return storedMode;
      }
    }
    return DEFAULT_STREAMS_VIEW_MODE;
  });
  const [networkInterfaceOptions, setNetworkInterfaceOptions] = useState<string[]>([...DEFAULT_NETWORK_INTERFACE_OPTIONS]);
  const [isNetworkInterfacesLoading, setIsNetworkInterfacesLoading] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(STREAMS_VIEW_MODE_STORAGE_KEY, streamsViewMode);
    } catch (err) {
      console.error("Failed to persist streams view mode", err);
    }
  }, [streamsViewMode]);

  const loadNetworkInterfaces = useCallback(
    async (currentValue?: string) => {
      setIsNetworkInterfacesLoading(true);
      try {
        const response = await authorizedFetch("/api/network/interfaces");
        const payload = (await response.json().catch(() => null)) as { interfaces?: string[]; error?: string } | null;
        if (!response.ok) {
          throw new Error(payload?.error ?? "Unable to load network interfaces");
        }

        const defaults = [...DEFAULT_NETWORK_INTERFACE_OPTIONS];
        const fetched = Array.from(
          new Set((payload?.interfaces ?? []).map((ip) => ip.trim()).filter((ip) => ip && !defaults.includes(ip)))
        ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

        let nextOptions = [...defaults, ...fetched];
        const trimmedCurrent = currentValue?.trim();
        if (trimmedCurrent && !nextOptions.includes(trimmedCurrent)) {
          nextOptions = [...nextOptions, trimmedCurrent];
        }

        setNetworkInterfaceOptions(nextOptions);
      } catch (err) {
        console.error("Failed to load network interfaces", err);
        setNetworkInterfaceOptions((prev) => {
          const defaults = [...DEFAULT_NETWORK_INTERFACE_OPTIONS];
          const extras = prev.filter((option) => !defaults.includes(option));
          return [...defaults, ...extras];
        });
      } finally {
        setIsNetworkInterfacesLoading(false);
      }
    },
    [authorizedFetch]
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStream, setEditingStream] = useState<StreamSummary | null>(null);
  const [configReadOnly, setConfigReadOnly] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState<StreamSummary[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<StreamDeleteProgressPayload | null>(null);
  const bulkDeleteSourceRef = useRef<EventSource | null>(null);
  const bulkDeleteDeletedIdsRef = useRef<string[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" } | null>(null);
  const [commandsInFlight, setCommandsInFlight] = useState<Record<string, "start" | "stop">>({});
  const [timelineStreamId, setTimelineStreamId] = useState<string | null>(null);
  const [calendarStreamId, setCalendarStreamId] = useState<string | null>(null);
  const [selectedStreamIds, setSelectedStreamIds] = useState<string[]>([]);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const timelineStream = useMemo(
    () => (timelineStreamId ? streams.find((stream) => stream.id === timelineStreamId) ?? null : null),
    [streams, timelineStreamId]
  );
  const calendarStream = useMemo(
    () => (calendarStreamId ? streams.find((stream) => stream.id === calendarStreamId) ?? null : null),
    [streams, calendarStreamId]
  );
  const selectedStreams = useMemo(() => {
    if (!selectedStreamIds.length) return [];
    const selectionSet = new Set(selectedStreamIds);
    return streams.filter((stream) => selectionSet.has(stream.id));
  }, [streams, selectedStreamIds]);

  const selectedStream = useMemo(
    () => selectedStreams[selectedStreams.length - 1] ?? null,
    [selectedStreams]
  );
  const isTimelineOpen = Boolean(timelineStreamId);
  const isCalendarOpen = Boolean(calendarStreamId);
  const editingStreamState = editingStream ? getStreamState(editingStream.id) : undefined;
  const editingStreamMessage = editingStream ? getStreamStateMessage(editingStream.id) : undefined;
  const isEditingStreamRunning =
    isStreamRunningState(editingStreamState, editingStreamMessage);
  const streamConfigLocked = Boolean(editingStream && isStreamConfigReadOnly(
    editingStreamState, editingStreamMessage, commandsInFlight[editingStream.id]
  ));
  const dialogReadOnly = configReadOnly || streamConfigLocked;
  const selectedConfigReadOnly = Boolean(selectedStream && isStreamConfigReadOnly(
    getStreamState(selectedStream.id), getStreamStateMessage(selectedStream.id), commandsInFlight[selectedStream.id]
  ));

  useEffect(() => {
    if (dialogOpen && streamConfigLocked) setConfigReadOnly(true);
  }, [dialogOpen, streamConfigLocked]);

  useEffect(() => {
    const trimmed = editingStream?.networkInterface?.trim();
    if (trimmed) {
      setNetworkInterfaceOptions((prev) => {
        if (prev.includes(trimmed)) {
          return prev;
        }
        return [...prev, trimmed];
      });
    }
  }, [editingStream]);

  const invalidateStreams = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["streams"] });
  }, [queryClient]);


  // Subscribe to stream list changes from WebSocket
  useEffect(() => {
    const unsubscribe = subscribeToStreamListChanges((message) => {
      console.log("[StreamsPage] Stream list changed:", message.data);
      invalidateStreams();
    });
    return unsubscribe;
  }, [invalidateStreams]);

  // Subscribe to platform list changes from WebSocket (affects platform names in streams)
  useEffect(() => {
    const unsubscribe = subscribeToPlatformListChanges((message) => {
      console.log("[StreamsPage] Platform list changed:", message.data);
      invalidateStreams();
    });
    return unsubscribe;
  }, [invalidateStreams]);

  useEffect(() => {
    if (!timelineStreamId) {
      return;
    }
    if (!streams.some((stream) => stream.id === timelineStreamId)) {
      setTimelineStreamId(null);
    }
  }, [streams, timelineStreamId]);

  useEffect(() => {
    if (!calendarStreamId) {
      return;
    }
    if (!streams.some((stream) => stream.id === calendarStreamId)) {
      setCalendarStreamId(null);
    }
  }, [streams, calendarStreamId]);

  useEffect(() => {
    if (streamsViewMode !== "Table") {
      setSelectedStreamIds([]);
    }
  }, [streamsViewMode]);

  useEffect(() => {
    setSelectedStreamIds((current) => current.filter((id) => filteredStreams.some((stream) => stream.id === id)));
  }, [filteredStreams]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await authorizedFetch(`/api/streams/${id}`, {
        method: "DELETE"
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? "Unable to delete stream";
        throw new Error(message);
      }
    },
    onSuccess: () => {
      invalidateStreams();
    }
  });

  const closeBulkDeleteStream = useCallback(() => {
    if (bulkDeleteSourceRef.current) {
      bulkDeleteSourceRef.current.close();
      bulkDeleteSourceRef.current = null;
    }
  }, []);

  const resetBulkDeleteState = useCallback(() => {
    closeBulkDeleteStream();
    bulkDeleteDeletedIdsRef.current = [];
    setBulkDeleteProgress(null);
  }, [closeBulkDeleteStream]);

  useEffect(() => () => closeBulkDeleteStream(), [closeBulkDeleteStream]);

  const commandMutation = useMutation({
    mutationFn: async ({
      streamId,
      command
    }: {
      streamId: string;
      command: "start" | "stop";
    }) => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15_000);
      let response: Response;
      try {
        response = await authorizedFetch(`/api/player/command`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            streamId,
            command
          }),
          signal: controller.signal
        });
      } catch (error) {
        if (controller.signal.aborted) {
          throw new Error(`Stream ${command} command timed out.`);
        }
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = typeof payload?.error === "string" ? payload.error : "Unable to process stream command";
        throw new Error(message);
      }
    },
    onMutate: ({ streamId, command }) => {
      setCommandsInFlight((current) => ({ ...current, [streamId]: command }));
    },
    onSuccess: (_data, variables) => {
      if (variables.command === "stop") {
        setStreamState(variables.streamId, "unknown");
      } else {
        setStreamState(variables.streamId, "unknown", "Starting");
      }
      invalidateStreams();
      setSnackbar({
        open: true,
        message: variables.command === "start" ? "Stream start command accepted." : "Stream stop command accepted.",
        severity: "success"
      });
    },
    onError: (err) => {
      console.error("Stream command failed", err);
      const message = err instanceof Error ? err.message : "Unable to process stream command.";
      setSnackbar({ open: true, message, severity: "error" });
    },
    onSettled: (_data, _error, variables) => {
      setCommandsInFlight((current) => {
        if (current[variables.streamId] !== variables.command) {
          return current;
        }
        const next = { ...current };
        delete next[variables.streamId];
        return next;
      });
    }
  });

  // The per-stream controls stop showing activity as soon as the live stream
  // state reaches the command's result. Apply that same rule to the toolbar so
  // it does not wait on the slower HTTP response after the state is up to date.
  const commandsAwaitingState = useMemo(() => {
    const pending: Record<string, "start" | "stop"> = {};
    for (const [streamId, command] of Object.entries(commandsInFlight)) {
      const state = getStreamState(streamId);
      const isRunning = isStreamRunningState(state, getStreamStateMessage(streamId)) || hasRTSPPublication(streamOutputStatsById.get(streamId));
      if ((command === "start" && !isRunning) || (command === "stop" && isRunning)) {
        pending[streamId] = command;
      }
    }
    return pending;
  }, [commandsInFlight, getStreamState, getStreamStateMessage, streamOutputStatsById]);

  const hasCommandInFlight = Object.keys(commandsAwaitingState).length > 0;
  const hasStartCommandInFlight = Object.values(commandsAwaitingState).includes("start");
  const hasStopCommandInFlight = Object.values(commandsAwaitingState).includes("stop");

  const handleOpenCreate = () => {
    if (!canManageStreams) {
      return;
    }
    setEditingStream(null);
    setConfigReadOnly(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = useCallback((stream: StreamSummary) => {
    if (!canManageStreams) {
      return;
    }
    setEditingStream(stream);
    setConfigReadOnly(isStreamConfigReadOnly(
      getStreamState(stream.id), getStreamStateMessage(stream.id), commandsInFlight[stream.id]
    ));
    setDialogOpen(true);
  }, [canManageStreams, getStreamState, getStreamStateMessage, commandsInFlight]);

  const handleToolbarEdit = useCallback(() => {
    if (!canManageStreams || !selectedStream) {
      return;
    }
    handleOpenEdit(selectedStream);
  }, [canManageStreams, selectedStream, handleOpenEdit]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingStream(null);
    setConfigReadOnly(false);
  };

  const handleOpenDelete = (stream: StreamSummary) => {
    if (!canManageStreams) {
      return;
    }
    resetBulkDeleteState();
    setDeleteTargets([stream]);
    setDeleteError(null);
  };

  const handleToolbarDelete = useCallback(() => {
    if (!canManageStreams || selectedStreams.length === 0) {
      return;
    }
    resetBulkDeleteState();
    setDeleteTargets([...selectedStreams]);
    setDeleteError(null);
  }, [canManageStreams, resetBulkDeleteState, selectedStreams]);

  const handleCloseDeleteInternal = (force?: boolean) => {
    if (isBulkDeleteRunning && !force) {
      return;
    }
    setDeleteTargets([]);
    setDeleteError(null);
    resetBulkDeleteState();
  };

  const handleCloseDelete = () => {
    handleCloseDeleteInternal();
  };

  const isAnyDeleteTargetRunning = deleteTargets.some((stream) => {
    const state = getStreamState(stream.id);
    return state === "online" || state === "timeout";
  });
  const isBulkDeleteRunning = bulkDeleteProgress?.status === "running";
  const bulkDeleteProgressPercent =
    bulkDeleteProgress && bulkDeleteProgress.total > 0
      ? Math.round((bulkDeleteProgress.completed / bulkDeleteProgress.total) * 100)
      : 0;

  const handleConfirmDelete = () => {
    if (!canManageStreams || deleteTargets.length === 0) return;
    if (isAnyDeleteTargetRunning) {
      setDeleteError("Selected stream(s) are currently running. Stop them before deleting.");
      return;
    }
    setDeleteError(null);
    const ids = deleteTargets.map((stream) => stream.id);

    if (ids.length > 1) {
      const runBulkDelete = async () => {
        const response = await authorizedFetch("/api/streams/bulk-delete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids })
        });
        const payload = (await response.json().catch(() => null)) as { jobId?: string; total?: number; error?: string } | null;
        if (!response.ok || !payload?.jobId) {
          const message = payload?.error ?? "Unable to delete streams.";
          throw new Error(message);
        }

        bulkDeleteDeletedIdsRef.current = [];
        setBulkDeleteProgress({
          jobId: payload.jobId,
          total: payload.total ?? ids.length,
          completed: 0,
          failed: 0,
          status: "running"
        });

        const sseUrl = new URL(resolveApiPath(`/api/streams/bulk-delete/${payload.jobId}/events`), window.location.href);
        if (userManagementEnabled && tokens?.accessToken) {
          sseUrl.searchParams.set("access_token", tokens.accessToken);
        }
        const source = new EventSource(sseUrl.toString());
        bulkDeleteSourceRef.current = source;

        source.onmessage = (event) => {
          try {
            const update = JSON.parse(event.data) as StreamDeleteProgressPayload;
            setBulkDeleteProgress(update);
            if (update.currentId && !update.currentError) {
              if (!bulkDeleteDeletedIdsRef.current.includes(update.currentId)) {
                bulkDeleteDeletedIdsRef.current = [...bulkDeleteDeletedIdsRef.current, update.currentId];
              }
            }
            if (update.status !== "running") {
              closeBulkDeleteStream();
              invalidateStreams();
              if (update.failed > 0) {
                setDeleteError(`${update.failed} of ${update.total} deletions failed.`);
              } else {
                setSnackbar({
                  open: true,
                  message: `${update.total} streams removed.`,
                  severity: "success"
                });
                setSelectedStreamIds((current) =>
                  current.filter((id) => !bulkDeleteDeletedIdsRef.current.includes(id))
                );
                handleCloseDeleteInternal(true);
              }
            }
          } catch (err) {
            console.error("Failed to parse delete progress", err);
          }
        };

        source.onerror = (event) => {
          console.error("Bulk delete progress error", event);
          closeBulkDeleteStream();
          setBulkDeleteProgress((current) =>
            current ? { ...current, status: "failed" } : current
          );
          setDeleteError("Bulk delete progress stream interrupted.");
        };
      };

      void runBulkDelete().catch((err) => {
        console.error(err);
        const message = err instanceof Error ? err.message : "Unable to delete streams.";
        setDeleteError(message);
        resetBulkDeleteState();
      });
      return;
    }

    const runDeletes = async () => {
      for (const id of ids) {
        await deleteMutation.mutateAsync(id);
      }
    };
    void runDeletes()
      .then(() => {
        setSnackbar({
          open: true,
          message: `${deleteTargets[0].sensor} removed.`,
          severity: "success"
        });
        setSelectedStreamIds((current) => current.filter((id) => !ids.includes(id)));
        handleCloseDeleteInternal();
      })
      .catch((err) => {
        console.error(err);
        const message = err instanceof Error ? err.message : "Unable to delete stream.";
        setDeleteError(message);
      });
  };

  const handleToggleTimeline = useCallback((stream: StreamSummary) => {
    setTimelineStreamId((current) => (current === stream.id ? null : stream.id));
  }, []);

  const handleCloseTimeline = useCallback(() => {
    setTimelineStreamId(null);
  }, []);

  const handleToggleCalendar = useCallback((stream: StreamSummary) => {
    setCalendarStreamId((current) => (current === stream.id ? null : stream.id));
  }, []);

  const handleCloseCalendar = useCallback(() => {
    setCalendarStreamId(null);
  }, []);

  const handleOpenPlayer = useCallback(
    (stream: StreamSummary) => {
      navigate(`/player?streamId=${encodeURIComponent(stream.id)}`);
    },
    [navigate]
  );

  const isStreamStarting = useCallback((stream: StreamSummary) => {
    const streamState = getStreamState(stream.id);
    const streamStateMessage = getStreamStateMessage(stream.id);
    return isStreamStartingState(streamState, streamStateMessage);
  }, [getStreamState, getStreamStateMessage]);

  const runBulkCommand = useCallback(
    async (command: "start" | "stop") => {
      if (!canManageStreams) return;
      const targets =
        command === "start"
          ? selectedStreams.filter((stream) => !isStreamStarting(stream))
          : selectedStreams;
      if (!targets.length) return;
      for (const stream of targets) {
        try {
          await commandMutation.mutateAsync({ streamId: stream.id, command });
        } catch {
          // mutation already reports errors/snackbar
        }
      }
    },
    [canManageStreams, commandMutation, isStreamStarting, selectedStreams]
  );

  const handleStartSelected = useCallback(() => {
    void runBulkCommand("start");
  }, [runBulkCommand]);

  const handleStopSelected = useCallback(() => {
    void runBulkCommand("stop");
  }, [runBulkCommand]);

  const handleDuplicate = useCallback(async () => {
    if (!canManageStreams || selectedStreams.length !== 1) {
      return;
    }
    setIsDuplicating(true);
    const createdIds: string[] = [];
    try {
      for (const stream of selectedStreams) {
        const copySensor = `${stream.sensor.trim()}-Copy`;
        const {
          id: _streamId,
          platformName: _platformName,
          state: _state,
          sensor: _sensor,
          ...rest
        } = stream;
        const trimmedPlatformId = (stream.platformId ?? "").trim();
        const payload = {
          ...rest,
          sensor: copySensor,
          platformId: trimmedPlatformId,
          type: stream.type.trim(),
          url: stream.url.trim(),
          networkInterface: stream.networkInterface?.trim() ?? "",
          description: stream.description?.trim() ?? "",
          active: false,
          addPlatformMetadata: trimmedPlatformId ? Boolean(stream.addPlatformMetadata) : false
        };

        const response = await authorizedFetch("/api/streams", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          const message = data?.error ?? `Unable to duplicate stream ${stream.sensor}`;
          throw new Error(message);
        }

        const data = await response.json().catch(() => null);
        if (data && typeof data === "object") {
          if ("stream" in data && data.stream && typeof data.stream === "object") {
            const streamRecord = data.stream as StreamSummary;
            if (streamRecord.id) {
              createdIds.push(streamRecord.id);
            }
          } else if ("id" in data && typeof (data as Record<string, unknown>).id === "string") {
            createdIds.push((data as Record<string, unknown>).id as string);
          }
        }
      }

      if (createdIds.length > 0) {
        setSelectedStreamIds(createdIds);
      }
      const message =
        createdIds.length > 1
          ? `${createdIds.length} streams duplicated.`
          : `${selectedStreams[0].sensor.trim()}-Copy added.`;
      setSnackbar({ open: true, message, severity: "success" });
      invalidateStreams();
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : "Unable to duplicate stream.";
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setIsDuplicating(false);
    }
  }, [authorizedFetch, canManageStreams, invalidateStreams, selectedStreams]);

  const errorMessage = isError ? (error instanceof Error ? error.message : "Unable to load streams") : null;
  const selectionCount = selectedStreamIds.length;
  const hasAnySelection = selectionCount > 0;
  const hasSingleSelection = selectionCount === 1;
  const hasOnlyStartingSelection = useMemo(() => {
    if (!hasAnySelection) return false;
    return selectedStreams.every((stream) => isStreamStarting(stream));
  }, [hasAnySelection, isStreamStarting, selectedStreams]);
  const hasRunningSelection = useMemo(() => {
    if (!hasAnySelection) return false;
    return selectedStreams.some((stream) => {
      const state = getStreamState(stream.id);
      return isStreamRunningState(state, getStreamStateMessage(stream.id)) || hasRTSPPublication(streamOutputStatsById.get(stream.id));
    });
  }, [getStreamState, getStreamStateMessage, selectedStreams, hasAnySelection, streamOutputStatsById]);
  const startSelectedTooltip = hasOnlyStartingSelection ? "Selected streams are already starting" : "Start selected streams";

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box>
          <Box display="inline-flex" alignItems="center" gap={1}>
            <RssFeedIcon sx={{ color: "text.secondary" }} />
            <Typography variant="h5" sx={{ m: 0 }}>
              Streams
            </Typography>
            <Badge badgeContent={filteredStreams.length} color="primary" sx={{ ml: 1 }} />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: { xs: "none", lg: "block" } }}
          >
            Manage live feeds available to operators.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <Autocomplete
            multiple
            fullWidth
            value={selectedPlatformOptions}
            onChange={handlePlatformFilterChange}
            options={platformOptions}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterSelectedOptions
            disableCloseOnSelect
            loading={isPlatformsLoading}
            disabled={isPlatformsError && platformOptions.length === 0}
            sx={{ minWidth: { sm: 240 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by platform"
                placeholder={isPlatformsLoading ? "Loading platforms..." : "Select platforms"}
                size="small"
                error={Boolean(platformLoadErrorMessage)}
                helperText={platformLoadErrorMessage ?? undefined}
              />
            )}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={streamsViewMode}
            onChange={handleViewModeChange}
            aria-label="Streams view style"
            color="primary"
            sx={{ ml: { xs: 0, sm: 0.75 } }}
          >
            <ToggleButton value="Cards" aria-label="Cards view">
              <Tooltip title="Cards" describeChild>
                <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <DashboardCustomizeIcon fontSize="small" />
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="Table" aria-label="Table view">
              <Tooltip title="Table" describeChild>
                <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <TableRowsIcon fontSize="small" />
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="Masonry" aria-label="Masonry view">
              <Tooltip title="Masonry" describeChild>
                <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AppsIcon fontSize="small" />
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          {canManageStreams ? (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                ml: { xs: 0, sm: 0.75 },
                flexWrap: "nowrap",
                gap: 0.5,
                overflowX: { xs: "auto", sm: "visible" },
                justifyContent: { xs: "flex-start", md: "flex-end" },
                py: { xs: 0.25, sm: 0 }
              }}
            >
              {isNarrowToolbar ? (
                <Tooltip title="Add stream">
                  <span>
                    <IconButton color="primary" aria-label="Add stream" onClick={handleOpenCreate} size="small">
                      <AddBoxIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleOpenCreate}
                  fullWidth={false}
                  size="small"
                  sx={{
                    whiteSpace: "nowrap",
                    px: 3,
                    minWidth: "auto",
                    flexShrink: 0
                  }}
                >
                  Add stream
                </Button>
              )}
              {streamsViewMode === "Table" ? (
                <>
                  <Tooltip title="Duplicate stream">
                    <span>
                      <IconButton
                        color="primary"
                        aria-label="Duplicate stream"
                        onClick={handleDuplicate}
                        disabled={!canManageStreams || selectedStreams.length !== 1 || isDuplicating}
                        size="small"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={selectedConfigReadOnly ? "Show Config" : "Edit stream"}>
                    <span>
                      <IconButton
                        color="primary"
                        aria-label={selectedConfigReadOnly ? "Show Config" : "Edit stream"}
                        onClick={handleToolbarEdit}
                        disabled={!canManageStreams || !hasSingleSelection}
                        size="small"
                      >
                        {selectedConfigReadOnly ? <SettingsIcon /> : <EditIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete stream">
                    <span>
                      <IconButton
                        color="primary"
                        aria-label="Delete stream"
                        onClick={handleToolbarDelete}
                        disabled={!canManageStreams || !hasAnySelection}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={startSelectedTooltip}>
                    <span>
                      <IconButton
                        color="primary"
                        aria-label="Start streams"
                        onClick={handleStartSelected}
                        disabled={!canManageStreams || !hasAnySelection || hasRunningSelection || hasOnlyStartingSelection || hasCommandInFlight}
                        size="small"
                      >
                        {hasStartCommandInFlight ? (
                          <CircularProgress size={20} thickness={5} />
                        ) : (
                          <PlayCircleIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Stop selected streams">
                    <span>
                      <IconButton
                        color="primary"
                        aria-label="Stop streams"
                        onClick={handleStopSelected}
                        disabled={!canManageStreams || !hasAnySelection || !hasRunningSelection || hasCommandInFlight}
                        size="small"
                      >
                        {hasStopCommandInFlight ? (
                          <CircularProgress size={20} thickness={5} />
                        ) : (
                          <StopCircleIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : errorMessage ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : streams.length === 0 ? (
        <Alert severity="info">
          {canManageStreams
            ? "No streams configured yet. Add your first stream to get started."
            : "No streams are configured yet. Contact an administrator to add one."}
        </Alert>
      ) : hasPlatformFilter && filteredStreams.length === 0 ? (
        <Alert severity="info">No streams match the selected platforms.</Alert>
      ) : streamsViewMode === "Table" ? (
        <StreamTable
          streams={filteredStreams}
          canManageStreams={canManageStreams}
          canViewDetectionInfo={canViewDetectionInfo}
          commandsInFlight={commandsAwaitingState}
          streamBitratesById={visibleStreamBitratesById}
          fileSourceInfoByStream={fileSourceInfoByStreamId}
          onCommand={commandMutation.mutate}
          onOpenEdit={handleOpenEdit}
          onOpenDelete={handleOpenDelete}
          onOpenDetection={openDetection}
          onToggleTimeline={handleToggleTimeline}
          onToggleCalendar={handleToggleCalendar}
          onOpenPlayer={handleOpenPlayer}
          timelineStreamId={timelineStreamId}
          calendarStreamId={calendarStreamId}
          getStreamState={getStreamState}
          getStreamStateMessage={getStreamStateMessage}
          getOutputState={getOutputState}
          getOutputStateMessage={getOutputStateMessage}
          outputStatsByStream={streamOutputStatsById}
          selectedStreamIds={selectedStreamIds}
          onSelectionChange={setSelectedStreamIds}
          previewOpenAnimation="zoom"
        />
      ) : streamsViewMode === "Masonry" ? (
        <StreamMasonry
          streams={filteredStreams}
          canManageStreams={canManageStreams}
          canViewDetectionInfo={canViewDetectionInfo}
          commandsInFlight={commandsAwaitingState}
          streamBitratesById={visibleStreamBitratesById}
          fileSourceInfoByStream={fileSourceInfoByStreamId}
          onCommand={commandMutation.mutate}
          onOpenEdit={handleOpenEdit}
          onOpenDelete={handleOpenDelete}
          onOpenDetection={openDetection}
          onToggleTimeline={handleToggleTimeline}
          onToggleCalendar={handleToggleCalendar}
          onOpenPlayer={handleOpenPlayer}
          timelineStreamId={timelineStreamId}
          calendarStreamId={calendarStreamId}
          getStreamState={getStreamState}
          getStreamStateMessage={getStreamStateMessage}
          getOutputState={getOutputState}
          getOutputStateMessage={getOutputStateMessage}
          outputStatsByStream={streamOutputStatsById}
          previewOpenAnimation="zoom"
        />
      ) : (
        <StreamCards
          streams={filteredStreams}
          canManageStreams={canManageStreams}
          canViewDetectionInfo={canViewDetectionInfo}
          commandsInFlight={commandsAwaitingState}
          streamBitratesById={visibleStreamBitratesById}
          fileSourceInfoByStream={fileSourceInfoByStreamId}
          onCommand={commandMutation.mutate}
          onOpenEdit={handleOpenEdit}
          onOpenDelete={handleOpenDelete}
          onOpenDetection={openDetection}
          onToggleTimeline={handleToggleTimeline}
          onToggleCalendar={handleToggleCalendar}
          onOpenPlayer={handleOpenPlayer}
          timelineStreamId={timelineStreamId}
          calendarStreamId={calendarStreamId}
          getStreamState={getStreamState}
          getStreamStateMessage={getStreamStateMessage}
          getOutputState={getOutputState}
          getOutputStateMessage={getOutputStateMessage}
          outputStatsByStream={streamOutputStatsById}
          previewOpenAnimation="zoom"
        />
      )}

      {canManageStreams ? (
        <>
          <StreamFormDialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            editingStream={editingStream}
            streams={streams}
            platformOptions={platformOptions}
            isPlatformsLoading={isPlatformsLoading}
            isPlatformsError={isPlatformsError}
            platformsError={platformsError}
            networkInterfaceOptions={networkInterfaceOptions}
            isNetworkInterfacesLoading={isNetworkInterfacesLoading}
            loadNetworkInterfaces={loadNetworkInterfaces}
            authorizedFetch={authorizedFetch}
            resolveApiPath={resolveApiPath}
            accessToken={tokens?.accessToken}
            invalidateStreams={invalidateStreams}
            onShowSnackbar={(snackbarState) => setSnackbar(snackbarState)}
            isEditingStreamRunning={isEditingStreamRunning}
            readOnly={dialogReadOnly}
          />

          <Dialog open={deleteTargets.length > 0} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
            <DialogTitle>Delete stream{deleteTargets.length > 1 ? "s" : ""}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Typography>
                  {deleteTargets.length > 1
                    ? "Are you sure you want to delete these streams?"
                    : (
                      <>
                        Are you sure you want to delete <strong>{deleteTargets[0]?.sensor}</strong>?
                      </>
                    )}
                </Typography>
                {bulkDeleteProgress && deleteTargets.length > 1 ? (
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      Deleting {bulkDeleteProgress.completed} of {bulkDeleteProgress.total}
                      {bulkDeleteProgress.failed > 0 ? ` (${bulkDeleteProgress.failed} failed)` : ""}.
                    </Typography>
                    <LinearProgress variant="determinate" value={bulkDeleteProgressPercent} />
                  </Stack>
                ) : null}
                {deleteTargets.length > 1 ? (
                  <Box component="ul" sx={{ pl: 3, mb: 0, maxHeight: 220, overflowY: "auto" }}>
                    {deleteTargets.map((stream) => (
                      <li key={stream.id}>
                        <Typography variant="body2">{stream.sensor}</Typography>
                      </li>
                    ))}
                  </Box>
                ) : null}
                {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDelete} variant="outlined" disabled={deleteMutation.isPending || isBulkDeleteRunning}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                color="error"
                disabled={deleteMutation.isPending || isBulkDeleteRunning}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}

      <StreamEventsDrawer open={isTimelineOpen} onClose={handleCloseTimeline} stream={timelineStream} />

      <Drawer anchor="right" open={isCalendarOpen} onClose={handleCloseCalendar} keepMounted>
        <Box
          sx={{
            width: { xs: 280, sm: 320, md: 360 },
            display: "flex",
            flexDirection: "column",
            height: "100%"
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 2,
              mt: 1
            }}
          >
            <Box>
              <Typography variant="subtitle1">Stream calendar</Typography>
              {calendarStream ? (
                <Typography variant="body2" color="text.secondary">
                  {calendarStream.sensor}
                </Typography>
              ) : null}
            </Box>
            <IconButton size="small" onClick={handleCloseCalendar} aria-label="Close stream calendar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, py: 2 }}>
            {calendarStream ? (
              <StreamCalendar streamId={calendarStream.id} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a stream to view its calendar.
              </Typography>
            )}
          </Box>
        </Box>
      </Drawer>

      <StreamInfoDialog
        open={Boolean(detectionStream)}
        onClose={closeDetection}
        streamSensor={detectionStream?.sensor}
        streamId={detectionStream?.id}
        streamState={detectionStream ? getStreamState(detectionStream.id) : undefined}
        isLoading={isDetectionLoading}
        errorMessage={detectionErrorMessage}
        detectionStateLabel={detectionStateLabel}
        showDetectingSpinner={showDetectingSpinner}
        detectedStreamInfo={detectionData?.detectedStreamInfo}
        jsonViewTheme={jsonViewTheme}
      />

      <Snackbar
        open={Boolean(snackbar?.open)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar?.severity ?? "success"}>
          {snackbar?.message ?? ""}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StreamsPage;
